import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useActiveCompanyRole, useIsAdmin } from '@hooks/usePermission';
import { FiUsers, FiAlertTriangle, FiClock } from 'react-icons/fi';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { Button, buttonVariants } from '@components/Button';
import { Dropdown, borderedSelectStyles } from '@components/Dropdown';
import { Input } from '@components/Input';
import { RangeSlider } from '@components/RangeSlider';
import { SettingToggleCard } from '@components/SettingToggleCard';
import { addNotification, notificationStates } from '@components/Notification';
import { useTeamAttendanceSettings } from '@/hooks/useTeamAttendanceSettings';
import teamService from '@/services/team';

import {
  SettingsFieldCard,
  settingsFieldCardLayouts,
  SETTINGS_FIELD_CONTROL_WIDTH,
} from './SettingsFieldCard.component';

import styles from './AlertsNotificationsSection.module.scss';

const THRESHOLD_TICKS = ['0%', '25%', '50%', '75%', '100%'];

// No company-level fallback for these fields — a team either configures
// them or the corresponding alert stays off for that team.
const DEFAULT_ALERT_CONFIG = {
  productivityAlertEnabled: false,
  productivityPercentageThreshold: 50,
  loginAlertEnabled: false,
  gracePeriodMinutes: 10,
  earlyLoginThresholdMinutes: 10,
  criticalThresholdMinutes: 60,
};

export const AlertsNotificationsSection = () => {
  const userProfile = useSelector((state) => state.auth?.profile);
  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const role = useActiveCompanyRole();
  const isAdmin = useIsAdmin();
  const activeTeamId = useSelector((state) => state.team?.active?.activeTeamid);

  const [teamOptions, setTeamOptions] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);

  const {
    alertConfig: savedAlertConfig,
    isConfigured,
    loading: loadingAlertSettings,
  } = useTeamAttendanceSettings(selectedTeam?.value);

  const [alertConfig, setAlertConfig] = useState(DEFAULT_ALERT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Load the departments (teams) this user is allowed to configure — every
  // team in the company for Admins, only their own team(s) for Managers.
  useEffect(() => {
    let cancelled = false;

    const fetchTeams = async () => {
      if (!companyId || !userId) return;
      setLoadingTeams(true);

      const res = await teamService.getMemberTeamCollection({
        companyId,
        userId,
        role,
        companyRole: role,
      });

      if (cancelled) return;

      if (res.success) {
        const options = res.data.map((team) => ({
          value: team.docId,
          label: team.Team,
        }));
        setTeamOptions(options);
        setSelectedTeam(
          options.find((option) => option.value === activeTeamId) ||
            options[0] ||
            null
        );
      } else {
        addNotification({
          message: 'Failed to load departments. Please try again.',
          level: notificationStates.error,
        });
      }
      setLoadingTeams(false);
    };

    fetchTeams();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId, role]);

  // Sync the draft config from the live doc whenever the selected
  // department or its saved values change.
  useEffect(() => {
    setAlertConfig(savedAlertConfig || DEFAULT_ALERT_CONFIG);
  }, [selectedTeam?.value, savedAlertConfig]);

  const handleThresholdChange = (value) => {
    const clamped = Math.min(100, Math.max(0, value));
    setAlertConfig((prev) => ({
      ...prev,
      productivityPercentageThreshold: Number.isNaN(clamped) ? 0 : clamped,
    }));
  };

  const hasChanges = Boolean(
    selectedTeam &&
    JSON.stringify(alertConfig) !==
      JSON.stringify(savedAlertConfig || DEFAULT_ALERT_CONFIG)
  );

  const handleCancel = () => {
    setAlertConfig(savedAlertConfig || DEFAULT_ALERT_CONFIG);
  };

  const handleSave = async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Access denied: Only administrators can update alert settings',
        level: notificationStates.error,
      });
      return;
    }
    if (!hasChanges || saving) return;

    setSaving(true);
    const res = await teamService.saveTeamAttendanceSettings(
      selectedTeam.value,
      {
        alertConfig,
      }
    );
    if (res.success) {
      addNotification({
        message: `Alert settings saved for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message:
          res.error || 'Failed to save alert settings. Please try again.',
        level: notificationStates.error,
      });
    }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!selectedTeam || resetting) return;
    setResetting(true);
    const res = await teamService.resetTeamAttendanceSettings(
      selectedTeam.value
    );
    if (res.success) {
      addNotification({
        message: `Alerts turned off for "${selectedTeam.label}"`,
        level: notificationStates.success,
      });
    } else {
      addNotification({
        message: res.error || 'Failed to reset alerts. Please try again.',
        level: notificationStates.error,
      });
    }
    setResetting(false);
  };

  return (
    <>
      <SettingsFieldCard
        layout={settingsFieldCardLayouts.row}
        icon={<FiUsers size={16} color="var(--indigo-600)" />}
        label="Select Department"
        helperText="Choose the department you want to configure."
      >
        <Dropdown
          options={teamOptions}
          value={selectedTeam}
          onChange={setSelectedTeam}
          isLoading={loadingTeams}
          isDisabled={saving}
          width={SETTINGS_FIELD_CONTROL_WIDTH}
          selectStyles={borderedSelectStyles}
          placeholder="Select a department"
        />
      </SettingsFieldCard>

      <SettingToggleCard
        title="Productivity Alert"
        description="Notify admins/managers when an employee's productivity falls below the set threshold."
        checked={alertConfig.productivityAlertEnabled}
        onChange={(checked) =>
          setAlertConfig((prev) => ({
            ...prev,
            productivityAlertEnabled: checked,
          }))
        }
        disabled={saving || !selectedTeam || loadingAlertSettings}
      >
        {alertConfig.productivityAlertEnabled && (
          <SettingsFieldCard
            layout={settingsFieldCardLayouts.row}
            icon={<FiAlertTriangle size={16} color="var(--blue-600)" />}
            iconBg="blue-100"
            label="Productivity Threshold"
            helperText="Set the minimum productivity percentage. Alerts will be triggered when an employee's productivity falls below this value."
          >
            <div className={styles.thresholdBlock}>
              <div className={styles.thresholdRow}>
                <div className={styles.thresholdInputWrap}>
                  <Input
                    id="productivityThreshold"
                    type="number"
                    min={0}
                    max={100}
                    value={alertConfig.productivityPercentageThreshold}
                    onChange={(e) =>
                      handleThresholdChange(Number(e.target.value))
                    }
                    rightBlock={
                      <Text
                        variant={textVariantsEnum.Text_sm}
                        fontWeight={fontWeightEnum.Medium}
                        color="gray-500"
                      >
                        %
                      </Text>
                    }
                    disabled={saving}
                  />
                </div>
                <RangeSlider
                  min={0}
                  max={100}
                  value={alertConfig.productivityPercentageThreshold}
                  onChange={handleThresholdChange}
                  tickLabels={THRESHOLD_TICKS}
                  className={styles.slider}
                />
              </div>
            </div>
          </SettingsFieldCard>
        )}
      </SettingToggleCard>

      <SettingToggleCard
        title="Late & Early Login Alert"
        description="Notify admins/managers in real time when an employee checks in later or earlier than scheduled."
        checked={alertConfig.loginAlertEnabled}
        onChange={(checked) =>
          setAlertConfig((prev) => ({ ...prev, loginAlertEnabled: checked }))
        }
        disabled={saving || !selectedTeam || loadingAlertSettings}
      >
        {alertConfig.loginAlertEnabled && (
          <>
            <SettingsFieldCard
              layout={settingsFieldCardLayouts.row}
              icon={<FiClock size={16} color="var(--orange-600)" />}
              iconBg="orange-100"
              label="Grace Period (Late)"
              helperText="Minutes after the scheduled start time before a check-in counts as late and triggers an alert."
            >
              <Input
                type="number"
                min={0}
                value={alertConfig.gracePeriodMinutes}
                rightBlock="min"
                onChange={(e) =>
                  setAlertConfig((prev) => ({
                    ...prev,
                    gracePeriodMinutes: Number(e.target.value),
                  }))
                }
                disabled={saving}
              />
            </SettingsFieldCard>
            <SettingsFieldCard
              layout={settingsFieldCardLayouts.row}
              icon={<FiClock size={16} color="var(--success-600)" />}
              iconBg="success-100"
              label="Early Login Threshold"
              helperText="Minutes before the scheduled start time before a check-in counts as early and triggers an alert."
            >
              <Input
                type="number"
                min={0}
                value={alertConfig.earlyLoginThresholdMinutes}
                rightBlock="min"
                onChange={(e) =>
                  setAlertConfig((prev) => ({
                    ...prev,
                    earlyLoginThresholdMinutes: Number(e.target.value),
                  }))
                }
                disabled={saving}
              />
            </SettingsFieldCard>
            <SettingsFieldCard
              layout={settingsFieldCardLayouts.row}
              icon={<FiAlertTriangle size={16} color="var(--error-600)" />}
              iconBg="error-100"
              label="Critical Late Threshold"
              helperText="Minutes late before a check-in is flagged as a critical delay."
            >
              <Input
                type="number"
                min={0}
                value={alertConfig.criticalThresholdMinutes}
                rightBlock="min"
                onChange={(e) =>
                  setAlertConfig((prev) => ({
                    ...prev,
                    criticalThresholdMinutes: Number(e.target.value),
                  }))
                }
                disabled={saving}
              />
            </SettingsFieldCard>
          </>
        )}
      </SettingToggleCard>

      {isConfigured && (
        <button
          type="button"
          className={styles.resetLink}
          onClick={handleReset}
          disabled={resetting || saving}
        >
          Turn off all alerts for this department
        </button>
      )}

      <div className={styles.saveRow}>
        <Button
          text="Cancel"
          variant={buttonVariants.secondary}
          onClick={handleCancel}
          disabled={saving}
        />
        <Button
          text="Save Changes"
          variant={buttonVariants.primary}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        />
      </div>
    </>
  );
};

AlertsNotificationsSection.displayName = 'AlertsNotificationsSection';
